/**
 * Races — who you are, which is a different question from who you fight for.
 *
 * The order of the layers matters and is the whole design, so it is stated here
 * once rather than inferred from five files:
 *
 *   **Kin** is what family you belong to. Elves are one kin and four races;
 *   goblins are one kin and two. Kin drives the map legend and nothing else. It
 *   has no mechanics and no geometry — it is a label for a player reading a key.
 *
 *   **Race** is what you *are*: what caps your army, which currencies exist for
 *   you, what you may build, what you may field, which fourth branch your
 *   Academy grows — and, in `face`, what bone you are made of. Two goblin races
 *   share a kin and share almost nothing else; see `splitmoot` and `cogwarren`,
 *   which is the pair this whole file was checked against.
 *
 *   **House** is a kingdom and the leader who is its face. Everything *worn,
 *   cut, forged or held* lives there and none of it lives here. Houses are in
 *   `leaders.ts`. Their passives are a second layer on top of the race passives
 *   below, never a replacement for them.
 *
 *   **Kingdom** is generated elsewhere — `mapsea` grows the world and seats
 *   several rival kingdoms in each region. This file exposes what is needed to
 *   hang a race on a region and a house on a kingdom, and generates no kingdoms
 *   of its own. See `homeland` here and `leadersOfRace` in `leaders.ts`.
 *
 * ── The rule every race in this file had to pass ─────────────────────────────
 *
 * A race must change the *shape* of a constraint, not its size. "+10% timber" is
 * not a race, it is a difficulty setting wearing a hat. So every entry carries a
 * `binding` — the thing that actually stops you fielding one more company — and
 * no two races sharing a binding are allowed to be alike anywhere else. The
 * Ashen and Karrun-Dûr both bind on housing; the Ashen may spend coin they have
 * not got, and the dwarves earn a currency by *losing*. They are the same only
 * in the one place where being the same is the baseline.
 *
 * ── The rule the portraits imposed, which is stricter ────────────────────────
 *
 * `RaceFace` is **body**. Nothing cultural may appear in it. The moment a race
 * entry says "wears scrap iron", the second kingdom of that race has nowhere
 * left to differ and the accent colour is the only lever remaining — which is
 * precisely the failure the owner named. So there are no ornaments in this file,
 * no helmets, no braids and no metal. All of it is in `HouseLook`.
 *
 * The second lesson from the same contract is worth more than any amount of
 * anatomy: **`frameScale` is the strongest identifier at thumbnail size**, which
 * is where the kingdom-selection screen lives. This roster spends it
 * deliberately, from 0.74 — a Splitmoot sits small with air over its head — to
 * 1.24, a Moonbound crowding the arch. No two races sit within 0.03 of each
 * other, and `tests/races.test.ts` says so.
 *
 * ── What is wired and what is written ────────────────────────────────────────
 *
 * Honest, because a data file that promises eleven mechanics and delivers one is
 * worse than one that delivers one and says so. Every binding names the system
 * that enforces it in the passive's `structural` field, and that field says
 * **BUILT** when it is true, so an unwired promise is greppable rather than a
 * lie. Six of the eight now are:
 *
 *   `mana`           the Litany's pool — `recomputeManaCap`, `musterRoom`
 *   `irreplaceable`  one refusal in `reinforceCompany`
 *   `names`          a scribe in `stepConsecration`, spent in `recruitCompany`
 *   `groves`         `recomputePopCap` reads rings, not beds, and they winter
 *   `thralls`        `damageUnit` takes rather than kills; the pens are the muster
 *   `moot`           no cap anywhere, and `stepPress` charges for it
 *
 * **`crews` is not built.** The Cog-Warrens' machines want a per-unit toll
 * column that `data/units.ts` does not carry, and half of a toll — heads charged
 * and never given back, with no panel showing the running bill — is worse than
 * none. It falls through to beds and wages, which is the honest failure mode: a
 * race that plays as everybody else rather than one that plays wrong.
 */

import type { ResourceKey } from '../sim/types.ts';
import { HUMAN_BASELINE, type RaceFace } from './faces.ts';

// ── Identity ───────────────────────────────────────────────────────────────

/**
 * The family a race belongs to. A label for the map legend and the selection
 * screen's grouping, and deliberately coarser than `RaceId`: a player reading a
 * key wants seven colours, not eleven.
 */
export type KinId =
  | 'human'
  | 'dwarf'
  | 'elf'
  | 'goblin'
  | 'undead'
  | 'beast'
  | 'ratkin'
  | 'saurian'
  | 'giant';

export const KIN_NAMES: Record<KinId, string> = {
  human: 'Men',
  dwarf: 'Dwarves',
  elf: 'Elves',
  goblin: 'Goblins',
  undead: 'The Unburied',
  beast: 'Beastkin',
  /**
   * **`ratkin` was declared here and no race was ever one.** `racesOfKin`
   * answered with an empty list, the selection screen's kin grouping had a
   * label with nothing under it, and `factions.ts` had a whole faction — the
   * Gutter Congregation, `race: 'Ratmen'` — flying a banner the roster could not
   * account for. The Undertow fills it, which is why they are a ratkin people
   * rather than the fourth thing they might have been.
   */
  ratkin: 'Ratkin',
  saurian: 'Saurians',
  giant: 'Giants',
};

/**
 * The mechanical identity. This is the type every other branch names.
 *
 * Two goblin entries and four elven ones, because the owner's test for this pass
 * was *"even different goblin factions should be unique"* and the cheapest
 * honest way to pass it is to stop pretending they are one people.
 */
export type RaceId =
  | 'ashen'
  | 'vigil'
  | 'karrun'
  | 'verdant'
  | 'silver'
  | 'drowned'
  | 'crimson'
  | 'splitmoot'
  | 'cogwarren'
  | 'litany'
  | 'moonbound'
  // The east. See the block above `longnoon` for what a new people had to
  // prove before it was allowed on this list.
  | 'longnoon'
  | 'undertow'
  | 'thirstless';

/**
 * What actually stops you raising one more company.
 *
 * Every game of this kind has exactly one of these and almost every game has the
 * same one — beds and wages. Making it a per-race choice is the highest-leverage
 * decision in the design, because it changes what the *answer* to "I want a
 * bigger army" is, and therefore what the middle of a run is about.
 */
export type BindingId =
  /** Beds, wages and mouths, all three. The baseline every other is a departure from. */
  | 'housing'
  /**
   * A pool, and nothing else. No beds, no bread, no wage — a host of the
   * unburied costs standing capacity alone, and the capacity is raised by
   * buildings, research, the leader's level and what he carries. The owner's own
   * example, and the one that is built.
   */
  | 'mana'
  /**
   * A consecrated name per company, made one at a time and destroyed with the
   * company that carried it. The Vigil's army is bounded by a *production line*,
   * so a company lost costs a week rather than a purse.
   */
  | 'names'
  /**
   * No houses at all. Heads arrive as groves mature and die back through
   * Hardfrost, so the Choir's population is a curve with a season in it rather
   * than a number with a ceiling.
   */
  | 'groves'
  /**
   * Captives. The Drowned Court does not raise soldiers, it takes them: an enemy
   * left alive at low health and walked home is a thrall, and a battle won
   * cleanly is a battle wasted.
   */
  | 'thralls'
  /**
   * Nothing caps it. The warren breeds on its own and the constraint is
   * *stability* rather than supply — past a line the mob starts pulling down
   * your own buildings. The only people whose army can be too large.
   */
  | 'moot'
  /**
   * Crews. A Cog-Warren machine is paid for in goblins who do not come back —
   * the Hollow's toll, charged from the first Age.
   */
  | 'crews'
  /**
   * Nothing replaces a loss. The Silver Reaches may not reinforce, ever; a
   * thinned company stays thin until it is disbanded.
   */
  | 'irreplaceable';

export const BINDING_NAMES: Record<BindingId, string> = {
  housing: 'Beds and Wages',
  mana: 'Mana',
  names: 'Consecrated Names',
  groves: 'The Grove',
  thralls: 'Thralls',
  moot: 'The Moot',
  crews: 'Crews',
  irreplaceable: 'The Irreplaceable',
};

// ── Economy ────────────────────────────────────────────────────────────────

/**
 * The economy mask, and the argument for it being a mask.
 *
 * `RESOURCE_KEYS` is one shared, ordered, append-only list. It is iterated in a
 * dozen places, the hash walks its order, the storage clamp walks it every tick
 * and the save writes it verbatim. A per-race key *list* would mean a per-race
 * hash order, a per-race migration and a per-race save — three of the four
 * things in this codebase that have already been broken once by parallel work.
 *
 * So a race does not get its own keys. It gets a **mask over the shared list**:
 * a set saying which keys this people may hold, produce, trade and see.
 * Everything else keeps walking all of them in the same order, a masked key sits
 * at zero forever because nothing this race owns produces it and the market
 * refuses it, and the digest is bit-identical to what it was before this file
 * existed. `SAVE_VERSION` does not move, and the three appended keys are
 * `??`-defaulted like every other key added since version 6.
 *
 * The mask is enforced in four places and nowhere else, which is the measure of
 * how cheap it is:
 *
 *   1. the catalogue — a race is never offered a building that makes what it
 *      cannot hold, so nothing produces a masked key in the first place;
 *   2. the market — `Cmd.Trade` refuses a key off the mask, so it cannot be
 *      bought in through the back door;
 *   3. the storage clamp — a masked key is pinned at zero every tick, which is
 *      the belt to the catalogue's braces and costs one comparison;
 *   4. the strip — `hudResources(age, raceId)` intersects with the mask, so the
 *      Litany never has a bread bar and the Assize always has an ichor one.
 *
 * `tests/races.test.ts` asserts that no race is denied a key that any *shared*
 * cost in the game is priced in — Age costs, tech nodes, the common roster, unit
 * ranks. Without that the mask is a foot-gun: deny grain to the undead for
 * flavour and twenty Warhost nodes become silently unbuyable for one race.
 */
export interface RaceEconomy {
  /** Keys this people has that the common list does not give everybody. */
  grants: readonly ResourceKey[];
  /** Keys of the common set this people does not have. Checked by the test above. */
  denies: readonly ResourceKey[];
  /**
   * Whether corpses on your ground yield Souls.
   *
   * This is the honest half of the owner's *"only dark elves and undead should
   * have souls"*. Souls could not simply be masked off everybody else: they are
   * not a race currency, they are the *game's* currency — sixty tech nodes,
   * forty powers, twelve relics, every power tier and the Hollow's own bill are
   * priced in them, and re-keying that per race is a rewrite that would collide
   * with four branches in flight this pass.
   *
   * What *is* race-conditional is the channel. Only the soulbound reap: a Pale
   * Litany or Drowned Court settlement takes Souls off every body that falls on
   * its ground and swims in them by the third Age. Everybody else has exactly
   * two doors — the Soul Furnace, which is a building and a decision, and the
   * market, which is a price. That is a whole income stream existing or not
   * existing, which is the structural difference asked for. The alternative was
   * a multiplier, which is not.
   */
  reapsSouls: boolean;
}

// ── Passives ───────────────────────────────────────────────────────────────

/**
 * A race passive. The first of the two layers, and the one every kingdom of this
 * people shares.
 *
 * Modifiers are a flat bag exactly the way relics, tech nodes and hero passives
 * are, and are folded through `foldPermanent` at world creation for the same
 * reason those are — see the items note in COORDINATION.md. `src/data` may not
 * import `defaultMods()`, so whether a key multiplies or adds is decided by the
 * bag, not here: write the value you would write on a tech node.
 */
/**
 * ── The one overlap to watch, and it caught a real bug ───────────────────────
 *
 * `FactionDef.bonuses` already exists and is already applied at world init. A
 * race passive naming the same key **stacks with it**, silently: Karrun-Dûr's
 * faction bonus is `wallHp: 1.5` and the first draft of `karrun_deepwrights`
 * said `wallHp: 1.5` as well, which is 2.25× walls for a race whose card claims
 * one and a half. It surfaced as a *hero* test failing three files away, which
 * is exactly how this kind of thing is normally found.
 *
 * So: **a race passive may not name a scalar key its faction's bonus table
 * already sets.** Where the faction already says the thing, the passive says it
 * in prose and spends its numbers somewhere else. `tests/races.test.ts` asserts
 * it over the whole roster.
 *
 * The honest note is that this is a seam rather than a design. `FactionDef.bonuses`
 * is the older half of the same idea and should eventually be absorbed into this
 * layer, at which point the rule becomes unnecessary rather than merely enforced.
 * That is not this pass's work: `factions.ts` is read by the director, the
 * rosters and the renderer, and four branches are in flight.
 */
export interface RacePassive {
  id: string;
  name: string;
  /** Exact mechanical text. The player reads this; keep it true. */
  rules: string;
  mods?: Record<string, number>;
  /**
   * Set when this passive is a *shape* rather than a number, and is therefore
   * enforced by a system rather than by the bag. The string names the system, so
   * an unwired passive is greppable rather than a lie.
   */
  structural?: string;
}

/**
 * Where a race belongs on a generated world. `mapsea` matches against this.
 *
 * **Twelve, and the two new ones cost the map nothing.** `realm.ts` already had
 * `'jungle'` and `'desert'` as `RegionCharacter`s and was folding them into
 * `'forest'` and `'steppe'` on the way here, because there was nobody who
 * belonged in a rainforest or a sand sea in particular. There is now, so the
 * fold is gone: a jungle is its own country and a desert is its own country,
 * exactly as a marsh and a mountain already were.
 *
 * The consequence is worth stating plainly rather than discovering: this moves
 * two *existing* regions' peoples. The Greenmaw on Vashtaur's rain coast was
 * the Verdant Choir's and is the Long Noon's; the Long Thirst behind the
 * Cloudbreakers was the Moonbound's and is the Thirstless's. Neither region
 * moved, changed shape or changed terrain — only who is standing on it — and
 * that is the lever `REGION_SEEDS` documents in as many words: *to move a
 * people, change a character*. The Choir keeps the temperate woods and the
 * Moonbound keep the grass, which is a cleaner division than either had.
 */
export type Homeland =
  | 'heartland'
  | 'forest'
  | 'jungle'
  | 'mountain'
  | 'coast'
  | 'isles'
  | 'waste'
  | 'marsh'
  | 'steppe'
  | 'desert'
  | 'underground'
  | 'blight';

export interface RaceDef {
  id: RaceId;
  kin: KinId;
  /** The proper name. Oblique on purpose; the game's voice lives here. */
  name: string;
  /**
   * The plain one. `factions.ts` learned this first and its note is still the
   * argument: a player choosing for the first time wants to know whether they
   * are picking dwarves or ratmen, and where two races share a kin this line is
   * what says how they differ.
   */
  plainName: string;
  epithet: string;
  flavour: string;

  binding: BindingId;
  /** What the binding does, and which system does it. Player-facing. */
  bindingText: string;
  /**
   * True of a people whose pop is raised, not born — exempt from the villager
   * life cycle in `systems/demography.ts`: no births into the cradle, no
   * coming of age, no death by age, and every head a working head. Additive
   * and read in exactly one place (`agelessPeople`), so a race that omits it
   * simply lives and dies like everybody else. Today: the Pale Litany, whose
   * soldiers are already dead and whose villagers were dug up, not delivered.
   */
  ageless?: boolean;

  economy: RaceEconomy;
  /** Layer one of two. Every kingdom of this race has these. */
  passives: readonly RacePassive[];

  /** Building ids only this race may raise. Gated by its Academy branch. */
  buildings: readonly string[];
  /** Unit ids only this race may field. */
  units: readonly string[];

  /**
   * The name of this race's fourth Academy branch, and what it is for. Every
   * race gets one, beside Fortification, Warhost and Sacrament. See `RACE_TECH`
   * in `tech.ts` for why the sixty shared nodes were extended, not forked.
   */
  branchName: string;
  branchFlavour: string;

  homeland: Homeland;
  /** Which existing `FactionDef` this race plays as, until kingdoms land. */
  factionId: string;
  /** Body only. Everything worn, cut or forged is on the house. */
  face: RaceFace;
}

/** A race face is the baseline plus the fields where this people is not human. */
function face(id: string, over: Partial<RaceFace>): RaceFace {
  return { ...HUMAN_BASELINE, ...over, id };
}

export const RACES: readonly RaceDef[] = [
  // ── Men ──────────────────────────────────────────────────────────────────
  {
    id: 'ashen',
    kin: 'human',
    name: 'The Ashen Crown',
    plainName: 'Men — the imperial remnant',
    epithet: 'what the empire settled for',
    flavour:
      'An empire that could not decide whether it was dying, and so did both. The paperwork survived. Most other things did not.',
    binding: 'housing',
    bindingText:
      'Beds, wages and mouths, all three, and none of them forgiving. The baseline every other people is a departure from.',
    economy: { grants: [], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'ashen_arrears',
        name: 'The Long Arrears',
        rules:
          'Your treasury may go below zero, to a floor of 400 gold in debt. Debt costs morale for as long as it stands, and costs nothing else. Nobody else may spend coin they have not got.',
        structural: 'systems/settlement.ts — stepTreasury; the gold floor is race-conditional',
      },
      {
        id: 'ashen_clerks',
        name: 'Clerks',
        rules: 'Buildings cost 6% less and repairs are half a day quicker. Somebody has already filed for it.',
        mods: { buildCost: 0.94 },
      },
    ],
    buildings: ['chancery', 'levy_hall'],
    units: [],
    branchName: 'The Chancery',
    branchFlavour: 'Owe it, spend it, write the sum down for later.',
    homeland: 'heartland',
    factionId: 'ashen',
    // The reference face, deliberately. Everything else in the roster is read
    // against this one, so it is left almost bare: weathered, and no more.
    face: face('ashen', {
      skin: [0xb08a68, 0x86634a, 0x5d4433],
      frameScale: 1,
    }),
  },
  {
    id: 'vigil',
    kin: 'human',
    name: 'The Gilded Vigil',
    plainName: 'Men — a holy order gone to seed',
    epithet: 'nine hundred oaths and no auditor',
    flavour:
      'They kept every promise they made. They simply made rather a lot of them, and some of them to each other.',
    binding: 'names',
    bindingText:
      'A company cannot take the field without a consecrated name. Names are made one at a time, take real minutes, and are destroyed with the company that carried them. A company lost costs you a week, not a purse.',
    economy: { grants: [], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'vigil_consecration',
        name: 'Consecration',
        rules:
          'Companies are bounded by consecrated names, not by beds. Population cap is 40% lower and every soldier is worth it.',
        structural:
          'systems/companies.ts — musterRoom refuses without a name and recruitCompany spends one; systems/settlement.ts — stepConsecration is the scribe. BUILT.',
      },
      {
        id: 'vigil_sacrament',
        name: 'Sacrament',
        rules:
          'Powers hit 20% harder and cool 15% faster. Your soldiers are few, expensive and very hard to kill; the buildings are expensive too, because there is a right way to build them.',
        mods: { powerDamage: 1.2, powerCooldown: 0.85 },
      },
    ],
    buildings: ['scriptorium', 'lamp_house'],
    units: [],
    branchName: 'The Litany of Names',
    branchFlavour: 'Say it properly or do not say it.',
    homeland: 'heartland',
    factionId: 'vigil',
    // Human bone, worn thin. The tells are the hooded lid and the high gaunt
    // cheek — an ascetic reads as an ascetic long before the gold does.
    face: face('vigil', {
      faceLength: 1.08,
      cheek: 0.92,
      cheekHeight: 0.3,
      jaw: 0.9,
      chin: 0.82,
      lid: 0.36,
      eyeSlant: 0.9,
      iris: [0x8a6a1e, 0xb08a2a, 0xd0a838],
      skin: [0xd6bda2, 0xa88a70, 0x7b6350],
      hairGrowth: 'patchy',
      // Larger than the Ashen despite the thinner face: a mitre crowds the arch
      // and the frame has to leave it somewhere to go.
      frameScale: 1.04,
      neck: 0.94,
    }),
  },

  // ── Dwarves ──────────────────────────────────────────────────────────────
  {
    id: 'karrun',
    kin: 'dwarf',
    name: 'Karrun-Dûr',
    plainName: 'Dwarves — the deep holds',
    epithet: 'they went down to meet it',
    flavour:
      'They did not flee the dark. They went down to meet it, and came back up with better walls and a longer list.',
    binding: 'housing',
    bindingText:
      'Beds and wages, as Men have them — except the beds are delvings, which stack, and the accounts are kept by somebody who does not forget.',
    economy: { grants: ['grudges'], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'karrun_ledger',
        name: 'The Book of Grudges',
        rules:
          'Every building of yours brought down, every company wiped and every wall breached writes a Grudge. Grudges are a currency and they are the only thing your Academy branch will take. You cannot mine them, farm them or trade them. You earn them by losing.',
        structural: 'systems/settlement.ts — grudges accrue in the loss paths; RACE_TECH prices in them',
      },
      {
        id: 'karrun_deepwrights',
        name: 'Deepwrights',
        rules:
          'Walls have half again the health and every building may be upgraded one tier past anyone else. Everything you dig comes 10% faster. You are slow, and you were going to be anyway.',
        mods: { resourceRate: 1.1 },
      },
    ],
    buildings: ['grudge_hall', 'deep_delving'],
    units: [],
    branchName: 'The Reckoning',
    branchFlavour: 'It is written down. It will be settled.',
    homeland: 'mountain',
    factionId: 'karrun',
    // Small vault over a heavy jaw, which is half the silhouette; the rest is
    // the frame. A dwarf crowds the arch and has almost no neck to do it with.
    face: face('karrun', {
      cranium: 0.82,
      temple: 1.14,
      cheek: 1.2,
      cheekHeight: 0.62,
      jaw: 1.34,
      chin: 1.02,
      faceLength: 0.78,
      browRidge: 1.7,
      eyeSize: 0.9,
      eyeSpacing: 1.12,
      lid: 0.3,
      mouthWidth: 0.9,
      skin: [0xb87f5c, 0x8a5c42, 0x5f402e],
      hair: [0x5a2f18, 0x7a4522, 0x2b241d, 0x9a9a92],
      frameScale: 1.18,
      neck: 0.62,
      shoulder: 1.5,
    }),
  },

  // ── Elves ────────────────────────────────────────────────────────────────
  //
  // Four of them, and they are four because the owner named elves, dark elves
  // and blood elves separately and was right to. They share the kin and the long
  // ear and nothing else: one is grown, one cannot be replaced, one takes what
  // it fields and one drinks it.
  {
    id: 'verdant',
    kin: 'elf',
    name: 'The Verdant Choir',
    plainName: 'Elves — sylvan, and rooted in it',
    epithet: 'they sing to things that used to be trees',
    flavour:
      'They sing to things that used to be trees. Sometimes the things answer, and it is not reliably the same thing twice.',
    binding: 'groves',
    bindingText:
      'You may not build a house. Heads arrive as groves mature and die back through Hardfrost, so your population is a curve with a season in it rather than a number with a ceiling. Winter is not a bad harvest here. It is a bereavement.',
    economy: { grants: [], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'verdant_rootwork',
        name: 'Rootwork',
        rules:
          'No houses. Population is grown: each matured grove carries heads, and Hardfrost takes a share of them back. Groves compound — every completed grove raises the yield of all the others.',
        structural:
          'world.ts — recomputePopCap reads groves, not beds; systems/bindings.ts — raceBuildRefusal forbids the house; systems/settlement.ts — stepGroves is the Hardfrost dieback. BUILT.',
      },
      {
        id: 'verdant_quickening',
        name: 'The Quickening',
        rules: 'Everything grown comes 12% faster and your soldiers move quickly. None of them takes a hit well.',
        mods: { resourceRate: 1.12 },
      },
    ],
    buildings: ['heartgrove', 'thornwright_hall'],
    units: [],
    branchName: 'The Long Growth',
    branchFlavour: 'Nothing worth having is finished this year.',
    homeland: 'forest',
    factionId: 'verdant',
    face: face('verdant', {
      cranium: 1.05,
      temple: 0.9,
      cheek: 0.88,
      cheekHeight: 0.3,
      jaw: 0.72,
      chin: 0.62,
      faceLength: 1.1,
      browRidge: 0.5,
      eyeSize: 1.14,
      eyeSlant: 1.35,
      lid: 0.16,
      iris: [0x3d5a1e, 0x527a28, 0x84b444],
      ear: 'long',
      earLength: 1.85,
      lip: 0.85,
      skin: [0xa8a982, 0x7d8060, 0x565a42],
      hair: [0x3b3020, 0x5d5030, 0x8a7b4a, 0x2a2c1c],
      frameScale: 0.88,
      neck: 1.12,
      shoulder: 0.84,
    }),
  },
  {
    id: 'silver',
    kin: 'elf',
    name: 'The Silver Reaches',
    plainName: 'Elves — high, and fewer every year',
    epithet: 'art, pride, and a closed door',
    flavour:
      'Realms of art and ancient pride. There were more of them once. There is no mechanism by which there will be again.',
    binding: 'irreplaceable',
    bindingText:
      'You may not reinforce. A company that loses a man has lost him; the gap stays until the company is disbanded. Each of them is worth four of anybody else’s, and that is the arrangement rather than a consolation.',
    economy: { grants: [], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'silver_irreplaceable',
        name: 'The Irreplaceable',
        rules:
          'Reinforcement is unavailable to you. Companies are raised whole or not at all. Every model has three times the health and twice the damage of its equivalent, and costs rather more than that.',
        structural: 'systems/companies.ts — reinforceCompany. BUILT.',
      },
      {
        id: 'silver_attunement',
        name: 'Attunement',
        rules:
          'Powers reach a third further and cool a fifth faster, and every model is worth four of anybody else’s. Veterancy accrues at half rate; there is very little left to teach them.',
        mods: { powerRadius: 1.3, powerCooldown: 0.8, veterancyRate: 0.5 },
      },
    ],
    buildings: ['sky_lantern_hall', 'amberworks'],
    units: [],
    branchName: 'The Remembered Art',
    branchFlavour: 'Everything here was better the first time it was done.',
    homeland: 'isles',
    factionId: 'verdant',
    // The longest face and the smallest frame but one: a Silver head sits low in
    // the arch with air above it, which is the whole read at thumbnail size.
    face: face('silver', {
      cranium: 1.14,
      temple: 0.88,
      cheek: 0.84,
      cheekHeight: 0.22,
      jaw: 0.66,
      chin: 0.56,
      faceLength: 1.2,
      browRidge: 0.38,
      eyeSize: 1.2,
      eyeSlant: 1.6,
      lid: 0.1,
      iris: [0x8fa2ad, 0xc2d3db, 0xdbe8ee],
      ear: 'swept',
      earLength: 2.1,
      mouthWidth: 0.85,
      lip: 0.75,
      skin: [0xe0d6dc, 0xb6a9b4, 0x8a7d8a],
      hair: [0xd8d4c8, 0xa8a49a, 0x6e6a62, 0xf0ece0],
      frameScale: 0.84,
      neck: 1.22,
      shoulder: 0.8,
    }),
  },
  {
    id: 'drowned',
    kin: 'elf',
    name: 'The Drowned Court',
    plainName: 'Elves — dark, and seaborne with it',
    epithet: 'raiders, slavers, patient',
    flavour: 'They came inland by a route that is not on any map, and is not, strictly speaking, a route.',
    binding: 'thralls',
    bindingText:
      'You do not raise soldiers, you take them. An enemy left alive at low health and walked home is a thrall; a battle won cleanly is a battle wasted. Your host is bounded by what you have carried off, and it starves if it wins nothing.',
    economy: { grants: ['ichor'], denies: [], reapsSouls: true },
    passives: [
      {
        id: 'drowned_thralldom',
        name: 'Thralldom',
        rules:
          'Enemy models broken below a fifth of health on your ground are taken rather than killed. Thralls are your muster and your labour both. You farm almost nothing — grain yields a third — and the difference has to be raided for.',
        structural:
          'systems/combat.ts — damageUnit takes rather than kills; systems/companies.ts — musterRoom and recruitCompany spend the pens. BUILT. Thralls are the muster and not yet the labour.',
      },
      {
        id: 'drowned_soulbound',
        name: 'Soulbound',
        rules:
          'Corpses on your ground yield Souls; almost nobody else’s do. Ichor is drawn from what you have taken. Both are yours and nobody else’s.',
        mods: { soulGain: 1.4 },
      },
    ],
    buildings: ['thrall_pens', 'ichor_still'],
    units: [],
    branchName: 'The Shadow Coast',
    branchFlavour: 'Everything worth having is already somebody else’s.',
    homeland: 'coast',
    factionId: 'drowned',
    // The one race in the roster with no white in the eye. At thirty pixels that
    // is the entire identification and it costs nothing to draw.
    face: face('drowned', {
      cranium: 1.08,
      cheek: 0.86,
      cheekHeight: 0.28,
      jaw: 0.74,
      chin: 0.6,
      faceLength: 1.12,
      browRidge: 0.62,
      eyeSize: 1.08,
      eyeSlant: 1.45,
      lid: 0.2,
      pupil: 'pinpoint',
      sclera: 'dark',
      glow: 0.18,
      iris: [0x2a2338, 0x50456a, 0x1a1622],
      ear: 'fin',
      earLength: 1.7,
      lip: 0.8,
      skin: [0x4a5262, 0x333a48, 0x21262f],
      hide: 'wrapped',
      hair: [0x14161c, 0x2a2e38, 0x4a5060, 0x0e1014],
      frameScale: 0.92,
      neck: 1.1,
      shoulder: 0.9,
    }),
  },
  {
    id: 'crimson',
    kin: 'elf',
    name: 'The Crimson Assize',
    plainName: 'Elves — blood-drinkers, and orderly about it',
    epithet: 'a court that sits every night',
    flavour:
      'The thirst is not a curse, they will tell you. It is a schedule. They keep it the way other people keep accounts, and out of the same people.',
    binding: 'housing',
    bindingText:
      'Beds, as Men have them, and no bread at all. Your soldiers do not eat, they drink, and the Ichor is drawn out of your own population a head at a time. A large host is a small town.',
    economy: { grants: ['ichor'], denies: ['bread'], reapsSouls: true },
    passives: [
      {
        id: 'crimson_thirst',
        name: 'The Thirst',
        rules:
          'Grain upkeep is nothing; the host is kept on Ichor instead, drawn from your own people at a head per measure. Unpaid, a company’s thirst runs — and at the top of the clock it feeds itself, out of whoever is nearest.',
        structural: 'systems/settlement.ts — stepUpkeep pays in ichor and heads, not grain',
      },
      {
        id: 'crimson_assize',
        name: 'The Assize',
        rules: 'Your soldiers heal for a tenth of what they deal, and get faster the longer a fight runs.',
        mods: { lifesteal: 0.1, attackSpeed: 1.1 },
      },
    ],
    buildings: ['bloodcourt', 'ichor_cellar'],
    units: [],
    branchName: 'The Standing Order',
    branchFlavour: 'Everyone gets their turn. That is the mercy in it.',
    homeland: 'blight',
    factionId: 'drowned',
    // Upper tusks, small — fangs read at any size and no other elf has them.
    face: face('crimson', {
      cranium: 1.1,
      cheek: 0.9,
      cheekHeight: 0.26,
      jaw: 0.76,
      chin: 0.62,
      faceLength: 1.1,
      browRidge: 0.55,
      eyeSize: 1.06,
      eyeSlant: 1.5,
      lid: 0.24,
      pupil: 'slit',
      glow: 0.32,
      iris: [0x8f2418, 0xb33420, 0xd04a2a],
      /**
       * The one round ear in a kin of points, and the Assize gets it rather
       * than the Reaches for two reasons that agree.
       *
       * The mechanical one: at rail size — 46px, dimmed, eight elven rows
       * against a shared left edge — everything else that separates the Assize
       * is *inside* the silhouette. Slit pupils, upper tusks, the ember glow and
       * the warm iris are all detail, and detail is exactly what a dimmed
       * thumbnail does not show. The Reaches, by contrast, are already the
       * smallest frame but one and the palest body in the kin, so they carry at
       * that size without help. The one who needs the outline lever is the one
       * whose other tells vanish.
       *
       * The one that makes it not merely arbitrary: the Silver Reaches *are* the
       * pointed elf — ancient, attenuated, 2.1 of ear — and taking the point off
       * them would be taking away the thing they are. The Assize is the elf that
       * has stopped being quite an elf, and a heavy rounded ear says departure
       * on a body that already has fangs. `batwing` was rejected as both too
       * obvious and already the Splitmoot's.
       *
       * Shorter as well as rounder: a paddle at 1.75 would be enormous.
       */
      ear: 'lobed',
      earLength: 1.15,
      lip: 1.25,
      tusk: 'upper',
      tuskSize: 0.28,
      skin: [0xe2d2d6, 0xb49ea6, 0x836e78],
      hair: [0x1c1418, 0x3a2028, 0x6a3038, 0x0e0a0c],
      frameScale: 0.96,
      neck: 1.06,
      shoulder: 0.92,
    }),
  },

  // ── Goblins ──────────────────────────────────────────────────────────────
  //
  // The pair this file was written to prove. They share a kin, a skin ramp, an
  // ear family and a height, and below that they share nothing: one is a
  // population curve you are trying to keep under a line, the other is a salvage
  // loop that eats its own workforce. Neither can play the other's game.
  //
  // Their faces are built on the portrait contract's own hint — a small vault
  // over a heavy jaw and a big vault over a light one are two silhouettes nobody
  // will confuse, and either can be a goblin. The Splitmoot is the first and the
  // Cog-Warrens are the second, and they are the two smallest frames in the
  // roster and half a point apart in it.
  {
    id: 'splitmoot',
    kin: 'goblin',
    name: 'The Splitmoot',
    plainName: 'Goblins — the warren, and all of it at once',
    epithet: 'a vote taken by weight',
    flavour:
      'Nobody is in charge. Everybody is fairly sure they would be good at it. That is the entire political history of the Splitmoot.',
    binding: 'moot',
    bindingText:
      'There is no population cap and there are no wages. The warren breeds on its own, faster the more of it there is, and past a line it starts pulling your own buildings down out of sheer press. You are the only people whose army can be too large.',
    economy: { grants: ['scrap'], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'splitmoot_press',
        name: 'The Press',
        rules:
          'No population cap. Goblins arrive on their own and arrive faster the more of them there are. Above the settlement’s tolerance the mob damages your own buildings and does not stop until it has room. You cannot recruit a company; you release one from whatever the warren happens to be full of.',
        structural:
          'systems/settlement.ts — stepPopulation breeds uncapped and stepPress pulls the town down; systems/companies.ts — a company is released from the warren, not recruited into it. BUILT.',
      },
      {
        id: 'splitmoot_numbers',
        name: 'Numbers',
        rules:
          'Companies hold half again as many and each of them is worth about a third of a man. They rout early and they come back quickly.',
        mods: { popCap: 1.6, unitHp: 0.55, unitDamage: 0.62, moveSpeed: 1.15 },
      },
    ],
    buildings: ['breeding_pit', 'shout_post'],
    units: [],
    branchName: 'The Shouting',
    branchFlavour: 'Whoever is loudest was right all along.',
    homeland: 'waste',
    factionId: 'ironfang',
    face: face('splitmoot', {
      cranium: 0.7,
      temple: 0.84,
      cheek: 0.72,
      cheekHeight: 0.74,
      jaw: 1.3,
      chin: 0.52,
      faceLength: 0.8,
      browRidge: 1.6,
      muzzle: 0.35,
      eyeSize: 1.42,
      eyeSpacing: 1.22,
      eyeSlant: 0.7,
      lid: 0.06,
      pupil: 'pinpoint',
      iris: [0xb33420, 0xd04a2a, 0x8f2418],
      ear: 'batwing',
      earLength: 2.2,
      mouthWidth: 1.35,
      lip: 0.5,
      tusk: 'lower',
      tuskSize: 0.55,
      skin: [0x7c9e52, 0x5a7538, 0x3c5024],
      hide: 'warty',
      hairGrowth: 'patchy',
      hair: [0x2a2418, 0x40381f, 0x1a160e, 0x584a28],
      frameScale: 0.74,
      neck: 0.8,
      shoulder: 0.86,
    }),
  },
  {
    id: 'cogwarren',
    kin: 'goblin',
    name: 'The Cog-Warrens',
    plainName: 'Goblins — the works, and what it costs to run them',
    epithet: 'it worked last time',
    flavour:
      'They have solved every problem they have ever had, permanently, at least once. The solutions are still running. Some of them are still solving.',
    binding: 'crews',
    bindingText:
      'You field machines, not soldiers, and a machine is paid for in goblins who do not come back — the Hollow’s toll, charged from the first Age. You cannot build a wall. You build the ground the enemy has to cross.',
    economy: { grants: ['scrap'], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'cogwarren_crews',
        name: 'Crews',
        rules:
          'Every machine you field carries a toll: heads off your own shift, permanently, for as long as it runs. Your infantry is an afterthought and your engines are not.',
        structural: 'systems/settlement.ts — takeToll, generalised off the Hollow',
      },
      {
        id: 'cogwarren_salvage',
        name: 'Salvage',
        rules:
          'Scrap is your second currency and it comes from breaking things. Demolishing your own buildings refunds in Scrap rather than in what they were made of, and every engine and structure destroyed on your ground leaves a pile. A Cog-Warren economy is fed by wreckage, including its own.',
        structural: 'apply.ts — doDemolish; systems/combat.ts — the wreck path',
      },
    ],
    buildings: ['salvage_yard', 'cog_works'],
    units: [],
    branchName: 'The Works',
    branchFlavour: 'Improved twice since the last time it killed anyone.',
    homeland: 'underground',
    factionId: 'ironfang',
    face: face('cogwarren', {
      cranium: 1.18,
      temple: 1.06,
      cheek: 0.7,
      cheekHeight: 0.36,
      jaw: 0.84,
      chin: 0.56,
      faceLength: 0.86,
      browRidge: 0.85,
      muzzle: 0.2,
      eyeSize: 1.5,
      eyeSpacing: 1.16,
      eyeSlant: 1.1,
      lid: 0.04,
      pupil: 'round',
      iris: [0xd0a838, 0xb08a2a, 0xe8c477],
      ear: 'cropped',
      earLength: 0.9,
      mouthWidth: 1.05,
      lip: 0.55,
      tusk: 'lower',
      tuskSize: 0.3,
      skin: [0x7c9e52, 0x5a7538, 0x3c5024],
      hide: 'warty',
      hairGrowth: 'none',
      hair: [0x2a2418, 0x40381f, 0x1a160e, 0x584a28],
      frameScale: 0.8,
      neck: 0.9,
      shoulder: 1.05,
    }),
  },

  // ── The unburied ─────────────────────────────────────────────────────────
  {
    id: 'litany',
    kin: 'undead',
    name: 'The Pale Litany',
    plainName: 'Undead — a court that did not adjourn',
    epithet: 'attendance in the flesh being optional',
    flavour: 'The court never adjourned. It merely stopped requiring attendance in the flesh.',
    binding: 'mana',
    bindingText:
      'No beds, no bread, no wage. Every model you hold standing costs Mana, and Mana is a pool — widened by the Ossuary and the Black Choir, by your Academy branch, by the level of whoever leads you and by what he carries. Full, the host is as large as it will ever be until you widen it again.',
    // The dead are raised, not born, and do not wear out. See `RaceDef.ageless`.
    ageless: true,
    economy: { grants: [], denies: ['bread'], reapsSouls: true },
    passives: [
      {
        id: 'litany_pool',
        name: 'The Standing Pool',
        rules:
          'Your host is bounded by Mana rather than by housing, and it eats no grain and draws no wage. Mana is raised by unique buildings, by research, by your leader’s level and by item passives.',
        structural: 'systems/settlement.ts — stepMana; world.ts — recomputeManaCap. BUILT.',
      },
      {
        id: 'litany_tithe',
        name: 'The Grave Tithe',
        rules:
          'Corpses on your ground, friend or foe, yield Souls, and yield 40% more of them. Your soldiers are brittle, and they are also already dead.',
        mods: { soulGain: 1.4 },
      },
    ],
    buildings: ['ossuary', 'black_choir'],
    units: [],
    branchName: 'The Ossuary',
    branchFlavour: 'Widen the pool. Everything else follows from it.',
    homeland: 'marsh',
    factionId: 'litany',
    // No ear, no lip, no hair and no white in the eye. Almost everything that
    // makes a face read as a face has been taken out, which is the point, and
    // the frame is the second largest so that what is left is legible.
    face: face('litany', {
      cranium: 1.02,
      temple: 0.94,
      cheek: 1.16,
      cheekHeight: 0.2,
      jaw: 0.94,
      chin: 0.84,
      faceLength: 1.08,
      browRidge: 1.35,
      eyeSize: 0.78,
      eyeSpacing: 1.05,
      lid: 0,
      pupil: 'none',
      sclera: 'none',
      glow: 0.6,
      iris: [0x9fd8e8, 0x6ea8c0, 0xcdeef8],
      ear: 'none',
      earLength: 0,
      mouthWidth: 1.1,
      lip: 0,
      skin: [0xcdcdbc, 0x9d9d8c, 0x6f6f60],
      hide: 'bare',
      hairGrowth: 'none',
      hair: [0x6f6f60, 0x8a8a7a, 0x565648, 0x9d9d8c],
      frameScale: 1.1,
      neck: 1.14,
      shoulder: 1.02,
    }),
  },

  // ── Beastkin ─────────────────────────────────────────────────────────────
  {
    id: 'moonbound',
    kin: 'beast',
    name: 'The Moonbound',
    plainName: 'Beastkin — the long howl',
    epithet: 'they keep no walls',
    flavour: 'They keep no walls. A wall is a thing you die behind.',
    binding: 'housing',
    bindingText:
      'Beds and mouths as Men have them, and no granary worth the name. You cannot store: whatever you take is spent this season or it is gone.',
    economy: { grants: ['scrap'], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'moonbound_nohoard',
        name: 'No Hoard',
        rules:
          'Your storage cap is a fifth of anyone else’s and no building raises it. Everything is spent as it is earned. You cannot save toward an Age; you have to arrive at one already holding the price.',
        structural: 'world.ts — recomputeStorage and storageMul; the race multiplier is applied to the total. BUILT.',
      },
      {
        id: 'moonbound_hunger',
        name: 'Night Hunger',
        rules:
          'Your units are fast, hit hard and swing 15% quicker, and they learn from every night of it. Walls cost double and stand for half as long.',
        mods: { attackSpeed: 1.15, veterancyRate: 1.3 },
      },
    ],
    buildings: ['howl_stone', 'skinning_camp'],
    units: [],
    branchName: 'The Long Howl',
    branchFlavour: 'What you are carrying is what you have.',
    homeland: 'steppe',
    factionId: 'moonbound',
    // The largest frame and the only crest in the roster. A ram's horn is a
    // whole outline; half a horn would have been a mistake, so it is committed to.
    face: face('moonbound', {
      cranium: 0.9,
      temple: 1.06,
      cheek: 1.06,
      cheekHeight: 0.6,
      jaw: 1.24,
      chin: 0.74,
      faceLength: 1.3,
      browRidge: 1.62,
      muzzle: 0.78,
      eyeSize: 1.04,
      eyeSpacing: 1.28,
      eyeSlant: 0.85,
      lid: 0.28,
      pupil: 'goat',
      iris: [0x84b444, 0x699733, 0xd0a838],
      ear: 'tufted',
      earLength: 1.4,
      mouthWidth: 1.2,
      lip: 0.4,
      tusk: 'lower',
      tuskSize: 0.5,
      skin: [0x8e949f, 0x676d78, 0x454a54],
      hide: 'furred',
      hairGrowth: 'crest',
      hair: [0x3a3228, 0x5a4e3c, 0x8a7c64, 0x241e18],
      crest: 'horn-ram',
      crestSize: 1.3,
      frameScale: 1.24,
      neck: 1.32,
      shoulder: 1.46,
    }),
  },

  // ── The east ───────────────────────────────────────────────────────────────────────
  //
  // Three peoples for the eastern continent, and the bar each of them had to
  // clear is the one at the top of this file: a race must change the *shape* of
  // a constraint, not its size.
  //
  // **What is honest about them, said before anything else.** All three take a
  // binding that already exists and is already built, and two of the three
  // share one with an older people. That is deliberate rather than lazy, and
  // the reasoning is the file's own: the eight bindings are all spoken for, a
  // ninth is not a data change but a *system* — `src/sim/systems/bindings.ts`
  // and three call sites — and a binding declared here with nothing enforcing
  // it falls straight through to beds and wages. `crews` is already exactly
  // that and this file says so out loud. Three more unwired promises would
  // have been three peoples who *claim* to play differently and do not, which
  // is worse than three who genuinely do and have a cousin.
  //
  // So the rule that applies is the roster's second one: **no two races sharing
  // a binding may be alike anywhere else.** The Long Noon and the Verdant Choir
  // are both bound by something grown that dies back in winter, and share
  // nothing else — kin, country, faction, tempo, silhouette and Academy column
  // all differ, and one of them is a wood that sings while the other is a people
  // asleep on warm stone. The Undertow and the Splitmoot are both uncapped
  // warrens, and one is an argument while the other is a market. The Thirstless
  // and the Silver Reaches both cannot replace a loss, for opposite reasons:
  // the elves because there is no second draft of anybody, the giants because
  // the next one is ninety years off and still growing.
  {
    id: 'longnoon',
    kin: 'saurian',
    name: 'The Long Noon',
    plainName: 'Saurians — the terraced coasts',
    epithet: 'they are waiting for the right hour',
    flavour:
      'They keep no appointment sooner than a century and have never once been late for one. It is not that they are slow. It is that they watched what haste did to everybody else and took notes.',
    binding: 'groves',
    bindingText:
      'You may not build a house. Your people sleep on warm stone, so heads arrive as sun-terraces are laid and go back into the dark when the cold comes. Your population is a curve with a winter in it, and the winter is not a bad harvest — it is a sleep that some of them do not finish.',
    economy: { grants: [], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'longnoon_terraces',
        name: 'The Terraces',
        rules:
          'No houses. Population is laid rather than built: every completed sun-terrace carries heads and raises the yield of all the others, and Hardfrost takes a share of them back until the sun returns.',
        structural:
          'world.ts — recomputePopCap counts a terrace as a grove (economy, popCap); systems/bindings.ts — raceBuildRefusal forbids the house; systems/settlement.ts — stepGroves is the dieback. BUILT.',
      },
      {
        id: 'longnoon_patience',
        name: 'Patience',
        rules:
          'Every model has twice the health and learns half again as fast from every fight it survives. None of them will be hurried: they march and they swing a fifth slower than anybody else on the board.',
        mods: { unitHp: 2, veterancyRate: 1.5, attackSpeed: 0.8, moveSpeed: 0.8 },
      },
    ],
    buildings: ['sun_terrace', 'augur_stone'],
    units: [],
    branchName: 'The Long Basking',
    branchFlavour: 'Nothing worth doing is finished this century.',
    homeland: 'jungle',
    factionId: 'rotmoot',
    // A long heavy skull with a real muzzle, a bone frill above it, and no ear
    // at all — three changes to the *outline* at once, which is the only thing
    // a 46px dimmed row shows. The amber ramp keeps them off the goblins' green,
    // which is the roster's only other scaled-and-toothed silhouette.
    face: face('longnoon', {
      cranium: 0.92,
      temple: 1.02,
      cheek: 0.86,
      cheekHeight: 0.55,
      jaw: 1.12,
      chin: 0.7,
      faceLength: 1.16,
      browRidge: 1.25,
      muzzle: 0.62,
      eyeSize: 0.86,
      eyeSpacing: 1.18,
      eyeSlant: 1.2,
      lid: 0.42,
      pupil: 'slit',
      iris: [0xd0a838, 0xa8811e, 0xe8c477],
      ear: 'none',
      earLength: 0,
      mouthWidth: 1.3,
      lip: 0,
      skin: [0xa8813e, 0x7c5c28, 0x50391a],
      hide: 'scaled',
      hairGrowth: 'none',
      hair: [0x50391a, 0x7c5c28, 0x3a2a12, 0x8f6d34],
      crest: 'frill',
      crestSize: 1.15,
      frameScale: 1.14,
      neck: 1.16,
      shoulder: 1.12,
    }),
  },
  {
    id: 'undertow',
    kin: 'ratkin',
    name: 'The Undertow',
    plainName: 'Ratkin — the delta, and everything under it',
    epithet: 'the tide goes out and they do not',
    flavour:
      'Nobody built the city under the Tidemouth. It accreted. There is a market in it that has never once closed, and a good deal of it is below the waterline, which the traders regard as a matter of scheduling.',
    binding: 'moot',
    bindingText:
      'There is no population cap and there are no wages. The warren breeds on its own, faster the more of it there is, and past a line it starts pulling your own buildings down out of sheer press. You do not recruit a company; you open a sluice and see what comes out.',
    economy: { grants: ['scrap'], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'undertow_flood',
        name: 'The Flood',
        rules:
          'No population cap. Heads arrive on their own and arrive faster the more of them there are. Above the settlement’s tolerance the press damages your own buildings and does not stop until it has room. A muster is the only thing that relieves it.',
        structural:
          'systems/settlement.ts — stepPopulation breeds uncapped and stepPress pulls the town down; systems/companies.ts — a company is released from the warren rather than recruited into it. BUILT.',
      },
      {
        id: 'undertow_tally',
        name: 'The Long Tally',
        rules:
          'Scrap is your second currency and the market is your first. Every building you pull down and every wreck on your ground is worth Scrap, and your people move a quarter faster because the routes under the city are older than the streets over it. Each of them is worth about two thirds of a man.',
        structural: 'apply.ts — doDemolish; systems/combat.ts — the wreck path',
        mods: { moveSpeed: 1.25, unitHp: 0.6, unitDamage: 0.7 },
      },
    ],
    buildings: ['sump_market', 'weir_gate'],
    units: [],
    branchName: 'The Long Tally',
    branchFlavour: 'It has all been counted. Most of it twice.',
    homeland: 'marsh',
    factionId: 'gutter',
    // The smallest frame in the roster by four points, a real muzzle, and the
    // only round paddle ear on a head this size. A rat reads as a rat from the
    // outline alone, which is the whole requirement at rail size.
    face: face('undertow', {
      cranium: 0.84,
      temple: 0.9,
      cheek: 0.7,
      cheekHeight: 0.66,
      jaw: 0.78,
      chin: 0.5,
      faceLength: 0.9,
      browRidge: 0.55,
      muzzle: 0.95,
      eyeSize: 0.72,
      eyeSpacing: 1.3,
      eyeSlant: 0.8,
      lid: 0.3,
      pupil: 'pinpoint',
      sclera: 'dark',
      glow: 0.12,
      iris: [0xb03a2a, 0x8a2a1c, 0xd05a3a],
      ear: 'round',
      earLength: 1.6,
      mouthWidth: 0.85,
      lip: 0.3,
      tusk: 'upper',
      tuskSize: 0.42,
      skin: [0x8a7c6a, 0x64584a, 0x413830],
      hide: 'furred',
      hairGrowth: 'patchy',
      hair: [0x3a3028, 0x554839, 0x1e1a16, 0x7a6c58],
      frameScale: 0.7,
      neck: 0.78,
      shoulder: 0.82,
    }),
  },
  {
    id: 'thirstless',
    kin: 'giant',
    name: 'The Thirstless',
    plainName: 'Giants — the ones still standing in the sand',
    epithet: 'ninety years to the next one',
    flavour:
      'The empire under the Glass Waste was theirs, and a few of them are still at their posts in it. Nobody has yet volunteered to tell them the empire fell.',
    binding: 'irreplaceable',
    bindingText:
      'You may not reinforce. A company that loses one has lost him, and the gap stays until the company is disbanded — because the replacement is ninety years old and is not ready. Each of them is worth four of anybody else’s, which is the arrangement rather than the consolation.',
    economy: { grants: [], denies: [], reapsSouls: false },
    passives: [
      {
        id: 'thirstless_ninety',
        name: 'Ninety Years',
        rules:
          'Reinforcement is unavailable to you. Companies are raised whole or not at all, and every model has four times the health and twice the damage of its equivalent. There are also half as many of them as anybody would like.',
        structural: 'systems/companies.ts — reinforceCompany. BUILT.',
      },
      {
        id: 'thirstless_stonefed',
        name: 'Stone-Fed',
        rules:
          'You neither drink nor bake. Grain upkeep is a third of anybody else’s and the host does not thirst on a march, which is why the Sand Sea is a road to you and a grave to everyone crossing it after you.',
        mods: { unitHp: 4, unitDamage: 2, popCap: 0.5, grainUpkeep: 0.34 },
      },
    ],
    buildings: ['standing_kin', 'salt_kiln'],
    units: [],
    branchName: 'The Slow Water',
    branchFlavour: 'It will come. It has always come.',
    homeland: 'desert',
    factionId: 'ironfang',
    // The largest frame in the roster, and everything under it is opposition: a
    // heavy jaw that clears the temple so the head does not read as a pear, a
    // brow that sinks the eyes into shadow, and small eyes at that. Sandstone
    // skin and a plate of bone over the crown, because a giant ought to read as
    // architecture before it reads as a face.
    face: face('thirstless', {
      cranium: 1.06,
      temple: 1.2,
      cheek: 1.28,
      cheekHeight: 0.58,
      jaw: 1.42,
      chin: 1.12,
      faceLength: 0.92,
      browRidge: 1.85,
      muzzle: 0.25,
      eyeSize: 0.72,
      eyeSpacing: 1.2,
      eyeSlant: 0.9,
      lid: 0.45,
      iris: [0xc9b48a, 0xa08a62, 0x6f5f42],
      ear: 'cropped',
      earLength: 0.7,
      mouthWidth: 1.15,
      lip: 0.7,
      tusk: 'lower',
      tuskSize: 0.35,
      skin: [0xb9a689, 0x8c7a60, 0x5c4f3c],
      hide: 'bare',
      hairGrowth: 'none',
      hair: [0x6b6152, 0x8d8271, 0x494237, 0xa89c88],
      crest: 'bone-plate',
      crestSize: 0.9,
      frameScale: 1.3,
      neck: 1.34,
      shoulder: 1.62,
    }),
  },
];

const BY_ID = new Map<RaceId, RaceDef>();
for (const r of RACES) BY_ID.set(r.id, r);

export const DEFAULT_RACE: RaceId = 'ashen';

export function race(id: string): RaceDef {
  return BY_ID.get(id as RaceId) ?? BY_ID.get(DEFAULT_RACE)!;
}

export function isRaceId(id: string): id is RaceId {
  return BY_ID.has(id as RaceId);
}

export function racesOfKin(kin: KinId): RaceDef[] {
  return RACES.filter((r) => r.kin === kin);
}

/** Every race whose homeland is this kind of country. The `mapsea` seam. */
export function racesOfHomeland(h: Homeland): RaceDef[] {
  return RACES.filter((r) => r.homeland === h);
}

/**
 * The bridge to the world as it stands today.
 *
 * `FactionDef` still exists, still carries the accent colours, the rosters and
 * the architecture, and is still what `World.faction` points at. A race does not
 * replace it — a race is the layer *above* it, and until kingdoms land the two
 * are resolved through this pair of functions. Where several races play as one
 * faction, the first declared is what a bare faction id resolves to.
 */
export function raceOfFaction(factionId: string): RaceId {
  for (const r of RACES) if (r.factionId === factionId) return r.id;
  return DEFAULT_RACE;
}

export function factionOfRace(id: string): string {
  return race(id).factionId;
}

/** Whether this people's dead yield souls where they fall. */
export function raceReapsSouls(id: string): boolean {
  return race(id).economy.reapsSouls;
}

// ── The economy mask ───────────────────────────────────────────────────────

/**
 * Keys every people has: everything in `RESOURCE_KEYS` that is not granted to
 * somebody in particular.
 *
 * Built by subtraction rather than written out, so appending a key to
 * `RESOURCE_KEYS` without granting it to anybody makes it common — which is the
 * safe default and the one that cannot silently break a cost line.
 */
const GRANTED_TO_SOMEBODY = new Set<ResourceKey>();
for (const r of RACES) for (const k of r.economy.grants) GRANTED_TO_SOMEBODY.add(k);

/** Is this key somebody's private currency rather than everybody's? */
export function isRaceCurrency(k: ResourceKey): boolean {
  return GRANTED_TO_SOMEBODY.has(k);
}

const MASKS = new Map<RaceId, ReadonlySet<ResourceKey>>();

/**
 * Which of the shared keys this people may hold, produce, trade and see.
 *
 * Cached per race and never handed out for writing. Deterministic and free of
 * any world state, so it is safe to call from a hot loop — and it is called from
 * the storage clamp, which runs every tick over every key.
 */
export function raceEconomyMask(id: string, allKeys: readonly ResourceKey[]): ReadonlySet<ResourceKey> {
  const def = race(id);
  const hit = MASKS.get(def.id);
  if (hit) return hit;
  const denied = new Set<ResourceKey>(def.economy.denies);
  const granted = new Set<ResourceKey>(def.economy.grants);
  const out = new Set<ResourceKey>();
  for (const k of allKeys) {
    if (denied.has(k)) continue;
    if (GRANTED_TO_SOMEBODY.has(k) && !granted.has(k)) continue;
    out.add(k);
  }
  MASKS.set(def.id, out);
  return out;
}
